import { useState } from 'react';

import { DataTable, Paginator, FilterBar } from 'components';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import {
  CATEGORY_OPTIONS,
  ClaimExpansion,
  columnsFor,
} from '../DirectClaims/DirectClaims.ui';
import { displayDateTime } from '../DirectClaims/format';
import { ClosedTicket, ClosedTicketsParams } from './ClosedTickets.types';

// The GlobeMed report's styles: the table and its expanded row are the same.
import '../DirectClaims/direct-claims.scss';
import './closed-tickets.scss';

// The rows come a page at a time, latest service date first, so sorting on a
// column would only reorder the page on screen: the columns are not sortable.
const columnsWithoutSort = (category: string) =>
  columnsFor(category).map(({ sortable, ...column }: any) => column);

interface ClosedTicketsUiProps {
  tickets: ClosedTicket[];
  totalRecords: number;
  lastSyncedAt: string | null;
  loading: boolean;
  errorMessage: string;
  reference: string;
  params: ClosedTicketsParams;
  startDate: Date | null;
  endDate: Date | null;
  handlers: {
    handleSearchChange: (name: string, value: string) => void;
    handleSubmitSearch: () => void;
    handleStartDateChange: (event: any) => void;
    handleEndDateChange: (event: any) => void;
    handleCategoryChange: (value: string) => void;
    handlePageChange: (event: any) => void;
  };
}

/*
 * GlobeMed's closed claims as stored in our database, a page at a time. The
 * columns and the expanded row are the live report's, so the two read alike.
 */
export default function ClosedTicketsUi({
  tickets,
  totalRecords,
  lastSyncedAt,
  loading,
  errorMessage,
  reference,
  params,
  startDate,
  endDate,
  handlers,
}: ClosedTicketsUiProps) {
  const [expandedRows, setExpandedRows] = useState<any>(null);

  const hasFilters =
    !!params.startDate ||
    !!params.endDate ||
    !!params.reference ||
    params.category !== 'ALL';

  return (
    <div className="direct-claims closed-tickets">
      <FilterBar
        search={{
          inputs: [
            {
              name: 'reference',
              placeholder: 'Member ID',
              value: reference,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 4,
        }}
        filters={[
          {
            kind: 'date',
            name: 'startDate',
            placeholder: 'Service date from',
            selectionMode: 'single',
            // Day first, matching the dates in the table.
            dateFormat: 'dd/mm/yy',
            value: startDate,
            maxDate: endDate ?? undefined,
            onChange: handlers.handleStartDateChange,
            width: 3,
          },
          {
            kind: 'date',
            name: 'endDate',
            placeholder: 'Service date to',
            selectionMode: 'single',
            dateFormat: 'dd/mm/yy',
            value: endDate,
            minDate: startDate ?? undefined,
            onChange: handlers.handleEndDateChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'category',
            placeholder: 'Category',
            value: params.category,
            options: CATEGORY_OPTIONS,
            onChange: (event: any) =>
              handlers.handleCategoryChange(event.value),
            width: 2,
          },
        ]}
      />

      {errorMessage && (
        <p className="direct-claims__error" role="alert">
          {errorMessage}
        </p>
      )}

      {lastSyncedAt && (
        <p className="direct-claims__muted closed-tickets__synced">
          Last updated from GlobeMed {displayDateTime(lastSyncedAt)}
        </p>
      )}

      <DataTable
        value={loading ? [] : tickets}
        columns={columnsWithoutSort(params.category) as any}
        dataKey="id"
        totalRecords={loading ? 0 : totalRecords}
        expandedRows={expandedRows}
        onRowToggle={(event: any) => setExpandedRows(event.data)}
        rowExpansionTemplate={ClaimExpansion}
        emptyMessage={
          loading
            ? 'Loading...'
            : hasFilters
            ? 'No closed tickets for these filters.'
            : 'No closed tickets yet.'
        }
        stripedRows
      />

      {!loading && totalRecords > 0 && (
        <Paginator
          first={params.first}
          rows={params.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handlers.handlePageChange}
        />
      )}
    </div>
  );
}
